// src/components/exercises/plankDetection.js
import { calculateAngle } from './exerciseDetection';

const HIP_BAR = 170;
const KNEE_BAR = 150;
const SHOULDER_MIN = 60;
const SHOULDER_MAX = 120;
const LIFT_FLOOR = 50;
const FLAT_MAX = 20;

const clamp01 = (v) => Math.max(0, Math.min(1, v));
const cross = (ax, ay, bx, by) => ax * by - ay * bx;

// Pure analysis of one frame. No state, no side effects.
// Returns everything PlankCamera needs to drive its own state machine.
export const analyzePlank = (landmarks, gravityAxisDeg, screenAngleDeg) => {
  const sh11 = landmarks[11], sh12 = landmarks[12];
  const hip23 = landmarks[23], hip24 = landmarks[24];
  const el13 = landmarks[13], el14 = landmarks[14];
  const knee25 = landmarks[25], knee26 = landmarks[26];
  const ank27 = landmarks[27], ank28 = landmarks[28];

  const leftVis = (sh11.visibility + hip23.visibility + knee25.visibility + ank27.visibility) / 4;
  const rightVis = (sh12.visibility + hip24.visibility + knee26.visibility + ank28.visibility) / 4;
  const useLeft = leftVis >= rightVis;

  const sh = useLeft ? sh11 : sh12;
  const el = useLeft ? el13 : el14;
  const hip = useLeft ? hip23 : hip24;
  const knee = useLeft ? knee25 : knee26;
  const ank = useLeft ? ank27 : ank28;

  const visOK = [sh, el, hip, knee, ank].every(p => p && p.visibility > 0.5);
  const avgVis = (sh.visibility + el.visibility + hip.visibility + knee.visibility + ank.visibility) / 5;

  if (!visOK) {
    return { visOK: false, currentFail: 'vis', fullPlankRaw: false, score: 0 };
  }

  const hipA = calculateAngle(sh, hip, knee);
  const kneeA = calculateAngle(hip, knee, ank);
  const shA = calculateAngle(el, sh, hip);

  const lineX = ank.x - sh.x, lineY = ank.y - sh.y;
  const isSag = cross(lineX, lineY, hip.x - sh.x, hip.y - sh.y) *
                cross(lineX, lineY, el.x - sh.x, el.y - sh.y) > 0;

  let bodyAxis = Math.atan2(ank.y - sh.y, ank.x - sh.x) * 180 / Math.PI;
  bodyAxis = ((bodyAxis % 180) + 180) % 180;
  let flatDev;
  if (gravityAxisDeg === null) {
    flatDev = Math.min(bodyAxis, 180 - bodyAxis);
  } else {
    let feed = ((gravityAxisDeg - screenAngleDeg) % 180 + 180) % 180;
    let d = Math.abs(bodyAxis - feed);
    if (d > 90) d = 180 - d;
    flatDev = 90 - d;
  }

  const glx = ank.x - el.x, gly = ank.y - el.y;
  const glen = Math.hypot(glx, gly) || 1;
  const perp = Math.abs(cross(glx, gly, sh.x - el.x, sh.y - el.y)) / glen;
  const bodyLen = Math.hypot(ank.x - sh.x, ank.y - sh.y) || 1;
  const lift = Math.round((perp / bodyLen) * 400);

  let currentFail = null;
  if (flatDev > FLAT_MAX) currentFail = 'flat';
  else if (kneeA < KNEE_BAR) currentFail = 'knee';
  else if (lift < LIFT_FLOOR) currentFail = 'lift';
  else if (shA < SHOULDER_MIN || shA > SHOULDER_MAX) currentFail = 'shoulder';
  else if (hipA < HIP_BAR) currentFail = isSag ? 'sag' : 'pike';

  const hipScore = clamp01((hipA - (HIP_BAR - 30)) / 30);
  const kneeScore = clamp01((kneeA - 120) / 30);
  const shScore = clamp01(1 - (Math.abs(shA - 90) - 40) / 30);
  const flatScore = clamp01(1 - (flatDev - 20) / 40);
  const liftScore = clamp01((lift - 20) / 50);
  const score = (hipScore + kneeScore + shScore + flatScore + liftScore) / 5 * clamp01(avgVis / 0.6);

  const fullPlankRaw = visOK && hipA >= HIP_BAR && kneeA >= KNEE_BAR &&
    shA >= SHOULDER_MIN && shA <= SHOULDER_MAX && flatDev <= FLAT_MAX && lift >= LIFT_FLOOR;

  return {
    visOK, currentFail, fullPlankRaw, score,
    hipA, kneeA, shA, lift, flatDev, dir: isSag ? 'sag' : 'pike'
  };
};

// Skeleton draw — identical connections/points to squats/JJ, kept here
// since plank's video is canvas-rendered (not raw <video>), unlike EnhancedCamera.
export const drawPlankSkeleton = (ctx, landmarks, mapX, mapY) => {
  const conns = [[11,12],[11,13],[13,15],[12,14],[14,16],
    [11,23],[12,24],[23,24],[23,25],[25,27],[24,26],[26,28]];
  ctx.strokeStyle = '#10b981';
  ctx.lineWidth = 3;
  ctx.lineCap = 'round';
  conns.forEach(([s, e]) => {
    const sp = landmarks[s], ep = landmarks[e];
    if (sp?.visibility > 0.5 && ep?.visibility > 0.5) {
      ctx.beginPath();
      ctx.moveTo(mapX(sp.x), mapY(sp.y));
      ctx.lineTo(mapX(ep.x), mapY(ep.y));
      ctx.stroke();
    }
  });
  ctx.fillStyle = '#3b82f6';
  [11,12,13,14,15,16,23,24,25,26,27,28].forEach(i => {
    const lm = landmarks[i];
    if (lm?.visibility > 0.5) {
      ctx.beginPath();
      ctx.arc(mapX(lm.x), mapY(lm.y), 5, 0, 2 * Math.PI);
      ctx.fill();
    }
  });
};